/**
 * Cloud shaders.
 *
 * 1. CLOUD_READ_GLSL / _WGSL: the read code of the cloud splats' GSplatFormat. It runs in the
 *    unified renderer's copy stage (the pass that writes each splat into the shared work buffer),
 *    so the result sorts and blends with the scan exactly like any other splat, and every
 *    render-stage effect (relighting, fog, regions, mirror clip, shadows) treats a cloud splat
 *    like a scan splat. Per splat it:
 *    - drifts the whole cloud with the wind and wraps it through the layer (clouds leaving one
 *      side come back on the other, faded out near the edges so the jump is never seen);
 *    - billows: a slow 3D value-noise displacement of each splat around its place in the cloud,
 *      convective lifting near the tops, and coverage / life-cycle erosion that dissolves a
 *      cloud from its rim inward (and grows it back from its core);
 *    - lights it: transmittance toward the sun by marching the layer's extinction grid
 *      (Beer-Lambert), multiple-scattering octaves (Wrenninge et al.) of a two-lobe
 *      Henyey-Greenstein phase function (bright, silver-lined rims when backlit), a beer-powder
 *      term that darkens sun-facing rims seen from the sunny side (Schneider, Nubis), and sky /
 *      ground ambient occluded by the cloud above (bright tops, grey bases).
 *    Splats never reach the shadow system's caster opacity (0.6), so clouds don't draw into the
 *    directional shadow maps; their shadows come from the projected map below.
 *
 * 2. CLOUD_SHADOW_GLSL / _WGSL: soft cloud shadows on the scan, part of the splat shadow
 *    receiving code (effects/shadows/splatShadowShader.ts), so every modifier that composes
 *    relighting gets them. Each splat looks up the layer's column optical depth where the ray
 *    toward the sun crosses the layer's middle (a projected cloud-shadow map, the classic
 *    flight-sim technique), drifted and wrapped with the clouds.
 */

/** Names of the per-splat streams (RGBA32F textures) of the cloud format. */
export const CLOUD_STREAMS = ['cloudA', 'cloudB', 'cloudC', 'cloudD', 'cloudE'] as const;

/** Component uniforms the read code uses (set per layer on its gsplat component). */
export const CLOUD_UNIFORMS = {
    box: 'uCloudBox',
    genBox: 'uCloudGenBox',
    grid: 'uCloudGrid',
    wind: 'uCloudWind',
    shape: 'uCloudShape',
    sun: 'uCloudSun',
    sunColor: 'uCloudSunColor',
    sky: 'uCloudSky',
    ground: 'uCloudGround',
    albedo: 'uCloudAlbedo',
    shade: 'uCloudShade',
    camera: 'uCloudCamera',
    look: 'uCloudLook',
    gridSize: 'uCloudGridSize',
    density: 'uCloudDensity',
    toWorld: 'uCloudToWorld',
    scanGrid: 'uCloudScanGrid',
    scanGridSize: 'uCloudScanGridSize',
    scanDensity: 'uCloudScanDensity',
    extra: 'uCloudExtra',
} as const;

// uCloudBox      xyz box size now (layer units), w 1 = dome
// uCloudGenBox   xyz box size the layer was generated for, w dome curvature (y drop per unit²).
//                The live box is a window into the generated one: a keyframed size generates once
//                for the largest box its keyframes reach, then growing reveals clouds and shrinking
//                hides them (soft edges, nothing stretches); clouds sit on the live box's floor.
// uCloudGrid     x / y local y range of the extinction grid, z extinction at R = 1, w coverage 0..1
// uCloudWind     xy wind drift (normalised, wrapped), z time (s, wrapped), w evolution 0..1
// uCloudShape    x opacity (density × fade), y softness 0..1, z edge fade width (normalised), w life cycle on
// uCloudSun      xyz unit vector toward the sun (layer space), w direct light 0..1 (sun below the horizon → 0)
// uCloudSunColor rgb sunlight (linear), w forward phase eccentricity g
// uCloudSky      rgb sky light from above (linear), w ambient strength
// uCloudGround   rgb light from below (linear), w silver-lining strength
// uCloudAlbedo   rgb sunlit colour (linear), w powder strength
// uCloudShade    rgb shaded colour (linear), w stylised (banded) 0 / 1
// uCloudCamera   xyz camera (layer space), w world units per layer unit (unused, 1)
// uCloudLook     x billow noise scale (world units), y grid cell (world units), z direct light gain, w life period (s)
// uCloudGridSize x / y / z extinction grid cells (y = texture array layers)
// uCloudDensity  the extinction grid: a 2D texture array, one x-z slice (repeating) per height level
// uCloudToWorld  layer space → world (for clearance)
// uCloudScanGrid / uCloudScanGridSize / uCloudScanDensity: the scan's density grid (scanSky.ts):
//                origin + cell size, cells + 1 = present; G = dilated count / threshold × 64
// uCloudExtra    x 1 = clearance from the dilated grid (clouds), 0 = the cell itself (fog may touch the
//                ground), y clearance strength, z crease (billow fold) shading strength, w 1 = needs
//                scan content under it (fog lies on the ground, never hangs past the scan's edge)
export const CLOUD_READ_GLSL = /* glsl */ `
uniform vec4 uCloudBox;
uniform vec4 uCloudGenBox;
uniform vec4 uCloudGrid;
uniform vec4 uCloudWind;
uniform vec4 uCloudShape;
uniform vec4 uCloudSun;
uniform vec4 uCloudSunColor;
uniform vec4 uCloudSky;
uniform vec4 uCloudGround;
uniform vec4 uCloudAlbedo;
uniform vec4 uCloudShade;
uniform vec4 uCloudCamera;
uniform vec4 uCloudLook;
uniform vec4 uCloudGridSize;
uniform highp sampler2DArray uCloudDensity;
uniform mat4 uCloudToWorld;
uniform vec4 uCloudScanGrid;
uniform vec4 uCloudScanGridSize;
uniform highp sampler2DArray uCloudScanDensity;
uniform vec4 uCloudExtra;

vec4 cl_a;
vec4 cl_b;
vec4 cl_c;
vec4 cl_d;
vec4 cl_e;
vec3 cl_pos;
vec3 cl_grid;
float cl_alpha;
float cl_sigma;
bool cl_ready = false;

uvec3 cl_pcg3d(uvec3 v) {
    v = v * 1664525u + 1013904223u;
    v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
    v ^= v >> 16u;
    v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
    return v;
}
float cl_hash(ivec3 c) {
    return float(cl_pcg3d(uvec3(c)).x) * (1.0 / 4294967296.0);
}
float cl_noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = p - i;
    vec3 u = f * f * (3.0 - 2.0 * f);
    ivec3 c = ivec3(i);
    float n000 = cl_hash(c);
    float n100 = cl_hash(c + ivec3(1, 0, 0));
    float n010 = cl_hash(c + ivec3(0, 1, 0));
    float n110 = cl_hash(c + ivec3(1, 1, 0));
    float n001 = cl_hash(c + ivec3(0, 0, 1));
    float n101 = cl_hash(c + ivec3(1, 0, 1));
    float n011 = cl_hash(c + ivec3(0, 1, 1));
    float n111 = cl_hash(c + ivec3(1, 1, 1));
    return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
}
// extinction (per world unit) at a grid position (x, z periodic; y clamped): hardware bilinear
// within a height slice, linear between the two nearest slices
float cl_extinction(vec3 g) {
    if (g.y < 0.0 || g.y > 1.0) return 0.0;
    float layers = uCloudGridSize.y;
    float ly = clamp(g.y * layers - 0.5, 0.0, layers - 1.0);
    float l0 = floor(ly);
    float l1 = min(l0 + 1.0, layers - 1.0);
    vec2 s = mix(textureLod(uCloudDensity, vec3(g.x, g.z, l0), 0.0).rg, textureLod(uCloudDensity, vec3(g.x, g.z, l1), 0.0).rg, ly - l0);
    float gate = clamp((uCloudGrid.w * 1.2 - s.g) / 0.2, 0.0, 1.0);
    return s.r * uCloudGrid.z * gate;
}
// 1 in free air, 0 inside solid scan content (a mountain, a sign): clouds never intersect the scan
float cl_clearance(vec3 p) {
    if (uCloudScanGridSize.w < 0.5) return 1.0;
    vec3 w = (uCloudToWorld * vec4(p, 1.0)).xyz;
    vec3 g = (w - uCloudScanGrid.xyz) / uCloudScanGrid.w;
    if (any(lessThan(g, vec3(0.0))) || any(greaterThanEqual(g, uCloudScanGridSize.xyz))) return 1.0;
    vec2 t = texelFetch(uCloudScanDensity, ivec3(int(g.x), int(g.z), int(g.y)), 0).rg;
    float dens = (uCloudExtra.x > 0.5 ? t.g : t.r) * (255.0 / 64.0);
    return 1.0 - uCloudExtra.y * smoothstep(0.6, 1.2, dens);
}
// fog: 1 with scan content (the ground) within a few cells below, fading with the gap; 0 past the
// scan's edge, where fog would hang in the void
float cl_support(vec3 p) {
    if (uCloudExtra.w < 0.5 || uCloudScanGridSize.w < 0.5) return 1.0;
    vec3 w = (uCloudToWorld * vec4(p, 1.0)).xyz;
    vec3 g = (w - uCloudScanGrid.xyz) / uCloudScanGrid.w;
    if (g.x < 0.0 || g.z < 0.0 || g.x >= uCloudScanGridSize.x || g.z >= uCloudScanGridSize.z || g.y < 0.0) return 0.0;
    int top = int(uCloudScanGridSize.y) - 1;
    int y0 = int(g.y);
    float support = 0.0;
    for (int k = 0; k < 8; k++) {
        int y = y0 - k;
        if (y < 0) break;
        if (y > top) continue;
        float dens = texelFetch(uCloudScanDensity, ivec3(int(g.x), int(g.z), y), 0).g * (255.0 / 64.0);
        support = max(support, smoothstep(0.6, 1.2, dens) * (1.0 - float(k) / 8.0));
    }
    return support;
}
// Henyey-Greenstein, scaled so the isotropic phase is 1
float cl_hg(float mu, float g) {
    float g2 = g * g;
    return (1.0 - g2) / pow(max(1.0 + g2 - 2.0 * g * mu, 1e-4), 1.5);
}

void cl_prepare() {
    cl_ready = true;
    cl_a = loadCloudA();
    cl_b = loadCloudB();
    cl_c = loadCloudC();
    cl_d = loadCloudD();
    cl_e = loadCloudE();
    vec3 box = uCloudBox.xyz;
    vec3 gb = uCloudGenBox.xyz;
    bool dome = uCloudBox.w > 0.5;
    // the whole cloud drifts with the wind and wraps through the generated layer
    vec2 a = fract(vec2(cl_a.x, cl_a.z) + uCloudWind.xy + 0.5) - 0.5;
    vec3 anchor = vec3(a.x * gb.x, cl_a.y * gb.y + 0.5 * (gb.y - box.y), a.y * gb.z);
    // the live box is the window: clouds fade out at its edges
    vec2 w = anchor.xz / max(box.xz, vec2(1e-4));
    float fw = max(uCloudShape.z, 1e-3);
    float edge;
    if (dome) {
        float r = length(w) * 2.0;
        edge = 1.0 - smoothstep(1.0 - 2.0 * fw, 1.0, r);
    } else {
        edge = (1.0 - smoothstep(0.5 - fw, 0.5, abs(w.x))) * (1.0 - smoothstep(0.5 - fw, 0.5, abs(w.y)));
    }
    vec3 off = cl_b.xyz;
    float sigma = cl_b.w;
    float t = uCloudWind.z;
    float evo = uCloudWind.w;
    float h = cl_e.x;
    float phase = cl_hash(ivec3(floor(cl_b.xyz * 7.3)));
    if (evo > 0.0) {
        // billowing: a slow, coherent 3D displacement field through each cloud
        float tt = t * (0.03 + 0.22 * evo);
        vec3 q = off / max(uCloudLook.x, 1e-3) + vec3(cl_e.w * 37.0);
        vec3 d = vec3(
            cl_noise(q + vec3(0.0, 0.0, tt)),
            cl_noise(q + vec3(17.3, 5.1, tt * 1.13)),
            cl_noise(q + vec3(-9.2, 31.7, tt * 0.87))
        ) - 0.5;
        off += d * sigma * (1.0 + 2.6 * evo);
        // convection: tops slowly lift and settle
        off.y += sigma * 0.9 * evo * h * (0.5 + 0.5 * sin(tt * 2.3 + phase * 6.2831853));
    }
    vec3 p = anchor + off;
    // a live box thinner than the generated one: what rises above its top dissolves
    float vedge = box.y < gb.y - 1e-3 ? 1.0 - smoothstep(0.2 * box.y, 0.5 * box.y, p.y) : 1.0;
    if (dome) p.y -= uCloudGenBox.w * dot(anchor.xz, anchor.xz);
    cl_pos = p;
    // undrifted position in the extinction grid (generation geometry)
    cl_grid = vec3(
        cl_a.x + 0.5 + cl_b.x / gb.x,
        (cl_a.y * gb.y + cl_b.y - uCloudGrid.x) / max(uCloudGrid.y - uCloudGrid.x, 1e-4),
        cl_a.z + 0.5 + cl_b.z / gb.z
    );
    // coverage grows clouds from their core; the life cycle erodes and regrows them
    float grow = clamp((uCloudGrid.w * 1.2 - cl_a.w) / 0.2, 0.0, 1.0);
    if (uCloudShape.w > 0.5) {
        float life = 0.5 + 0.5 * sin(6.2831853 * (t / max(uCloudLook.w, 1.0) + cl_e.w));
        grow = min(grow, smoothstep(0.08, 0.55, life));
    }
    // near the layer's edge a cloud dissolves from its rim inward (like a cloud evaporating),
    // instead of turning into a translucent ghost
    grow = min(grow, min(edge, vedge) * 1.1);
    float rim = cl_e.y;
    float vis = smoothstep(rim - 0.2, rim + 0.02, grow * 1.15);
    float soft = uCloudShape.y;
    float k = 0.72 + 0.6 * soft;
    cl_sigma = sigma * k * mix(0.7, 1.0, vis);
    float alpha = cl_d.w * uCloudShape.x * vis * smoothstep(0.0, 0.3, edge) * smoothstep(0.0, 0.3, vedge) / pow(k / 1.02, 1.5);
    // splats around the camera fade instead of filling the screen
    float dist = length(p - uCloudCamera.xyz);
    alpha *= smoothstep(cl_sigma * 1.2, cl_sigma * 4.0, dist);
    alpha *= cl_clearance(p) * cl_support(p);
    cl_alpha = min(alpha, 0.55);
}

vec3 getCenter() {
    cl_prepare();
    return cl_pos;
}

// (w, x, y, z), as the engine's read functions return it
vec4 getRotation() {
    if (!cl_ready) cl_prepare();
    return vec4(cl_c.w, cl_c.x, cl_c.y, cl_c.z);
}

vec3 getScale() {
    if (!cl_ready) cl_prepare();
    return cl_alpha > 0.003 ? cl_sigma * cl_d.xyz : vec3(0.0);
}

vec4 getColor() {
    if (!cl_ready) cl_prepare();
    if (cl_alpha <= 0.003) return vec4(0.0);
    vec3 L = uCloudSun.xyz;
    vec3 gb = uCloudGenBox.xyz;
    vec3 perWorld = vec3(1.0 / gb.x, 1.0 / max(uCloudGrid.y - uCloudGrid.x, 1e-4), 1.0 / gb.z);
    float cell = uCloudLook.y;
    // optical depth toward the sun: six steps, growing (the near cloud matters most)
    float od = 0.0;
    float ds = cell * 0.7;
    vec3 g = cl_grid;
    for (int i = 0; i < 6; i++) {
        g += L * perWorld * ds;
        od += cl_extinction(g) * ds;
        ds *= 1.65;
    }
    // sky light from above, occluded by the cloud over this splat
    float up = (cl_extinction(cl_grid + vec3(0.0, 1.5 * cell * perWorld.y, 0.0)) + cl_extinction(cl_grid + vec3(0.0, 4.0 * cell * perWorld.y, 0.0))) * cell * 2.2;
    float skyVis = exp(-up * 0.6);
    // how deep in the cloud this splat sits (powder)
    float here = cl_extinction(cl_grid) * cell;
    vec3 V = normalize(cl_pos - uCloudCamera.xyz);
    float mu = dot(V, L);
    float gF = uCloudSunColor.w;
    // multiple-scattering octaves: less extinction, less anisotropy, less energy per octave
    float S = 0.0;
    float a = 1.0;
    float b = 1.0;
    float c = 1.0;
    for (int i = 0; i < 3; i++) {
        float ph = mix(cl_hg(mu, gF * c), cl_hg(mu, -0.25 * c), 0.5);
        S += a * exp(-od * b) * ph;
        a *= 0.62;
        b *= 0.25;
        c *= 0.5;
    }
    // silver lining: thin, backlit edges glow
    S += uCloudGround.w * pow(max(mu, 0.0), 14.0) * exp(-od * 0.5) * cl_e.y * cl_e.y * 1.5;
    // beer-powder: sun-facing rims, seen with the sun behind the viewer, lose in-scattered light
    float powder = mix(1.0, 1.0 - exp(-here * 2.5), uCloudAlbedo.w * (0.5 - 0.5 * mu));
    // folds between billows are shaded, lobes catch the light
    float crease = cl_e.z;
    float fold = mix(1.0 - 0.5 * uCloudExtra.z, 1.0, crease);
    float direct = min(S * powder * uCloudSun.w * uCloudLook.z * mix(0.82, 1.06, crease), 1.8);
    if (uCloudShade.w > 0.5) {
        // stylised: three soft-edged bands, never darker than the shade colour
        direct = 0.36 + 0.36 * smoothstep(0.2, 0.26, direct) + 0.36 * smoothstep(0.58, 0.64, direct);
        fold = 1.0;
    }
    float height = clamp(0.25 + 0.75 * cl_e.x, 0.0, 1.0);
    vec3 ambient = mix(uCloudGround.rgb, uCloudSky.rgb, height) * uCloudSky.w * mix(0.45, 1.0, skyVis) * fold;
    vec3 lin = uCloudAlbedo.rgb * uCloudSunColor.rgb * direct + uCloudShade.rgb * ambient;
    vec3 gamma = pow(max(lin, vec3(0.0)), vec3(1.0 / 2.2));
    return vec4(min(gamma, vec3(3.9)), cl_alpha);
}
`;

export const CLOUD_READ_WGSL = /* wgsl */ `
uniform uCloudBox: vec4f;
uniform uCloudGenBox: vec4f;
uniform uCloudGrid: vec4f;
uniform uCloudWind: vec4f;
uniform uCloudShape: vec4f;
uniform uCloudSun: vec4f;
uniform uCloudSunColor: vec4f;
uniform uCloudSky: vec4f;
uniform uCloudGround: vec4f;
uniform uCloudAlbedo: vec4f;
uniform uCloudShade: vec4f;
uniform uCloudCamera: vec4f;
uniform uCloudLook: vec4f;
uniform uCloudGridSize: vec4f;
var uCloudDensity: texture_2d_array<f32>;
var uCloudDensitySampler: sampler;
uniform uCloudToWorld: mat4x4f;
uniform uCloudScanGrid: vec4f;
uniform uCloudScanGridSize: vec4f;
var uCloudScanDensity: texture_2d_array<f32>;
uniform uCloudExtra: vec4f;

var<private> cl_a: vec4f;
var<private> cl_b: vec4f;
var<private> cl_c: vec4f;
var<private> cl_d: vec4f;
var<private> cl_e: vec4f;
var<private> cl_pos: vec3f;
var<private> cl_grid: vec3f;
var<private> cl_alpha: f32;
var<private> cl_sigma: f32;
var<private> cl_ready: bool = false;

fn cl_pcg3d(vin: vec3u) -> vec3u {
    var v = vin * 1664525u + 1013904223u;
    v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
    v = v ^ (v >> vec3u(16u));
    v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
    return v;
}
fn cl_hash(c: vec3i) -> f32 {
    return f32(cl_pcg3d(bitcast<vec3u>(c)).x) * (1.0 / 4294967296.0);
}
fn cl_noise(p: vec3f) -> f32 {
    let i = floor(p);
    let f = p - i;
    let u = f * f * (3.0 - 2.0 * f);
    let c = vec3i(i);
    let n000 = cl_hash(c);
    let n100 = cl_hash(c + vec3i(1, 0, 0));
    let n010 = cl_hash(c + vec3i(0, 1, 0));
    let n110 = cl_hash(c + vec3i(1, 1, 0));
    let n001 = cl_hash(c + vec3i(0, 0, 1));
    let n101 = cl_hash(c + vec3i(1, 0, 1));
    let n011 = cl_hash(c + vec3i(0, 1, 1));
    let n111 = cl_hash(c + vec3i(1, 1, 1));
    return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
}
fn cl_extinction(g: vec3f) -> f32 {
    if (g.y < 0.0 || g.y > 1.0) { return 0.0; }
    let layers = uniform.uCloudGridSize.y;
    let ly = clamp(g.y * layers - 0.5, 0.0, layers - 1.0);
    let l0 = floor(ly);
    let l1 = min(l0 + 1.0, layers - 1.0);
    let s0 = textureSampleLevel(uCloudDensity, uCloudDensitySampler, vec2f(g.x, g.z), i32(l0), 0.0).rg;
    let s1 = textureSampleLevel(uCloudDensity, uCloudDensitySampler, vec2f(g.x, g.z), i32(l1), 0.0).rg;
    let s = mix(s0, s1, ly - l0);
    let gate = clamp((uniform.uCloudGrid.w * 1.2 - s.g) / 0.2, 0.0, 1.0);
    return s.r * uniform.uCloudGrid.z * gate;
}
fn cl_clearance(p: vec3f) -> f32 {
    if (uniform.uCloudScanGridSize.w < 0.5) { return 1.0; }
    let w = (uniform.uCloudToWorld * vec4f(p, 1.0)).xyz;
    let g = (w - uniform.uCloudScanGrid.xyz) / uniform.uCloudScanGrid.w;
    if (any(g < vec3f(0.0)) || any(g >= uniform.uCloudScanGridSize.xyz)) { return 1.0; }
    let t = textureLoad(uCloudScanDensity, vec2i(i32(g.x), i32(g.z)), i32(g.y), 0).rg;
    var raw = t.r;
    if (uniform.uCloudExtra.x > 0.5) { raw = t.g; }
    let dens = raw * (255.0 / 64.0);
    return 1.0 - uniform.uCloudExtra.y * smoothstep(0.6, 1.2, dens);
}
fn cl_support(p: vec3f) -> f32 {
    if (uniform.uCloudExtra.w < 0.5 || uniform.uCloudScanGridSize.w < 0.5) { return 1.0; }
    let w = (uniform.uCloudToWorld * vec4f(p, 1.0)).xyz;
    let g = (w - uniform.uCloudScanGrid.xyz) / uniform.uCloudScanGrid.w;
    let size = uniform.uCloudScanGridSize.xyz;
    if (g.x < 0.0 || g.z < 0.0 || g.x >= size.x || g.z >= size.z || g.y < 0.0) { return 0.0; }
    let top = i32(size.y) - 1;
    let y0 = i32(g.y);
    var support = 0.0;
    for (var k = 0; k < 8; k++) {
        let y = y0 - k;
        if (y < 0) { break; }
        if (y > top) { continue; }
        let dens = textureLoad(uCloudScanDensity, vec2i(i32(g.x), i32(g.z)), y, 0).g * (255.0 / 64.0);
        support = max(support, smoothstep(0.6, 1.2, dens) * (1.0 - f32(k) / 8.0));
    }
    return support;
}
fn cl_hg(mu: f32, g: f32) -> f32 {
    let g2 = g * g;
    return (1.0 - g2) / pow(max(1.0 + g2 - 2.0 * g * mu, 1e-4), 1.5);
}

fn cl_prepare() {
    cl_ready = true;
    cl_a = loadCloudA();
    cl_b = loadCloudB();
    cl_c = loadCloudC();
    cl_d = loadCloudD();
    cl_e = loadCloudE();
    let box = uniform.uCloudBox.xyz;
    let gb = uniform.uCloudGenBox.xyz;
    let dome = uniform.uCloudBox.w > 0.5;
    let a = fract(vec2f(cl_a.x, cl_a.z) + uniform.uCloudWind.xy + 0.5) - 0.5;
    let anchor = vec3f(a.x * gb.x, cl_a.y * gb.y + 0.5 * (gb.y - box.y), a.y * gb.z);
    let w = anchor.xz / max(box.xz, vec2f(1e-4));
    let fw = max(uniform.uCloudShape.z, 1e-3);
    var edge: f32;
    if (dome) {
        let r = length(w) * 2.0;
        edge = 1.0 - smoothstep(1.0 - 2.0 * fw, 1.0, r);
    } else {
        edge = (1.0 - smoothstep(0.5 - fw, 0.5, abs(w.x))) * (1.0 - smoothstep(0.5 - fw, 0.5, abs(w.y)));
    }
    var off = cl_b.xyz;
    let sigma = cl_b.w;
    let t = uniform.uCloudWind.z;
    let evo = uniform.uCloudWind.w;
    let h = cl_e.x;
    let phase = cl_hash(vec3i(floor(cl_b.xyz * 7.3)));
    if (evo > 0.0) {
        let tt = t * (0.03 + 0.22 * evo);
        let q = off / max(uniform.uCloudLook.x, 1e-3) + vec3f(cl_e.w * 37.0);
        let d = vec3f(
            cl_noise(q + vec3f(0.0, 0.0, tt)),
            cl_noise(q + vec3f(17.3, 5.1, tt * 1.13)),
            cl_noise(q + vec3f(-9.2, 31.7, tt * 0.87))
        ) - 0.5;
        off += d * sigma * (1.0 + 2.6 * evo);
        off.y += sigma * 0.9 * evo * h * (0.5 + 0.5 * sin(tt * 2.3 + phase * 6.2831853));
    }
    var p = anchor + off;
    var vedge = 1.0;
    if (box.y < gb.y - 1e-3) { vedge = 1.0 - smoothstep(0.2 * box.y, 0.5 * box.y, p.y); }
    if (dome) { p.y -= uniform.uCloudGenBox.w * dot(anchor.xz, anchor.xz); }
    cl_pos = p;
    cl_grid = vec3f(
        cl_a.x + 0.5 + cl_b.x / gb.x,
        (cl_a.y * gb.y + cl_b.y - uniform.uCloudGrid.x) / max(uniform.uCloudGrid.y - uniform.uCloudGrid.x, 1e-4),
        cl_a.z + 0.5 + cl_b.z / gb.z
    );
    var grow = clamp((uniform.uCloudGrid.w * 1.2 - cl_a.w) / 0.2, 0.0, 1.0);
    if (uniform.uCloudShape.w > 0.5) {
        let life = 0.5 + 0.5 * sin(6.2831853 * (t / max(uniform.uCloudLook.w, 1.0) + cl_e.w));
        grow = min(grow, smoothstep(0.08, 0.55, life));
    }
    grow = min(grow, min(edge, vedge) * 1.1);
    let rim = cl_e.y;
    let vis = smoothstep(rim - 0.2, rim + 0.02, grow * 1.15);
    let soft = uniform.uCloudShape.y;
    let k = 0.72 + 0.6 * soft;
    cl_sigma = sigma * k * mix(0.7, 1.0, vis);
    var alpha = cl_d.w * uniform.uCloudShape.x * vis * smoothstep(0.0, 0.3, edge) * smoothstep(0.0, 0.3, vedge) / pow(k / 1.02, 1.5);
    let dist = length(p - uniform.uCloudCamera.xyz);
    alpha *= smoothstep(cl_sigma * 1.2, cl_sigma * 4.0, dist);
    alpha *= cl_clearance(p) * cl_support(p);
    cl_alpha = min(alpha, 0.55);
}

fn getCenter() -> vec3f {
    cl_prepare();
    return cl_pos;
}

fn getRotation() -> vec4f {
    if (!cl_ready) { cl_prepare(); }
    return vec4f(cl_c.w, cl_c.x, cl_c.y, cl_c.z);
}

fn getScale() -> vec3f {
    if (!cl_ready) { cl_prepare(); }
    if (cl_alpha > 0.003) { return cl_sigma * cl_d.xyz; }
    return vec3f(0.0);
}

fn getColor() -> vec4f {
    if (!cl_ready) { cl_prepare(); }
    if (cl_alpha <= 0.003) { return vec4f(0.0); }
    let L = uniform.uCloudSun.xyz;
    let gb = uniform.uCloudGenBox.xyz;
    let perWorld = vec3f(1.0 / gb.x, 1.0 / max(uniform.uCloudGrid.y - uniform.uCloudGrid.x, 1e-4), 1.0 / gb.z);
    let cell = uniform.uCloudLook.y;
    var od = 0.0;
    var ds = cell * 0.7;
    var g = cl_grid;
    for (var i: i32 = 0; i < 6; i++) {
        g += L * perWorld * ds;
        od += cl_extinction(g) * ds;
        ds *= 1.65;
    }
    let up = (cl_extinction(cl_grid + vec3f(0.0, 1.5 * cell * perWorld.y, 0.0)) + cl_extinction(cl_grid + vec3f(0.0, 4.0 * cell * perWorld.y, 0.0))) * cell * 2.2;
    let skyVis = exp(-up * 0.6);
    let here = cl_extinction(cl_grid) * cell;
    let V = normalize(cl_pos - uniform.uCloudCamera.xyz);
    let mu = dot(V, L);
    let gF = uniform.uCloudSunColor.w;
    var S = 0.0;
    var a = 1.0;
    var b = 1.0;
    var c = 1.0;
    for (var i: i32 = 0; i < 3; i++) {
        let ph = mix(cl_hg(mu, gF * c), cl_hg(mu, -0.25 * c), 0.5);
        S += a * exp(-od * b) * ph;
        a *= 0.62;
        b *= 0.25;
        c *= 0.5;
    }
    S += uniform.uCloudGround.w * pow(max(mu, 0.0), 14.0) * exp(-od * 0.5) * cl_e.y * cl_e.y * 1.5;
    let powder = mix(1.0, 1.0 - exp(-here * 2.5), uniform.uCloudAlbedo.w * (0.5 - 0.5 * mu));
    let crease = cl_e.z;
    var fold = mix(1.0 - 0.5 * uniform.uCloudExtra.z, 1.0, crease);
    var direct = min(S * powder * uniform.uCloudSun.w * uniform.uCloudLook.z * mix(0.82, 1.06, crease), 1.8);
    if (uniform.uCloudShade.w > 0.5) {
        direct = 0.36 + 0.36 * smoothstep(0.2, 0.26, direct) + 0.36 * smoothstep(0.58, 0.64, direct);
        fold = 1.0;
    }
    let height = clamp(0.25 + 0.75 * cl_e.x, 0.0, 1.0);
    let ambient = mix(uniform.uCloudGround.rgb, uniform.uCloudSky.rgb, height) * uniform.uCloudSky.w * mix(0.45, 1.0, skyVis) * fold;
    let lin = uniform.uCloudAlbedo.rgb * uniform.uCloudSunColor.rgb * direct + uniform.uCloudShade.rgb * ambient;
    let gamma = pow(max(lin, vec3f(0.0)), vec3f(1.0 / 2.2));
    return vec4f(min(gamma, vec3f(3.9)), cl_alpha);
}
`;

// ── cloud shadows on the scan (splat shadow receiving) ────────────────────────────────────
// uCloudShadowParams[slot]  x strength (0 = slot unused), y optical depth at R = 1, z layer middle y, w layer bottom y (layer space)
// uCloudShadowLight[slot]   xyz toward the sun (layer space), w coverage
// uCloudShadowWind[slot]    xy wind drift (normalised), z 1 = dome, w edge fade width (normalised)
// uCloudShadowBox[slot]     xyz box size (layer units), w unused
// uCloudShadowMatrix[slot]  world → layer space
// uCloudShadowGlobal        x 1 = any slot in use, y 1 = relighting on (the shadow removes the sun's light)
export const CLOUD_SHADOW_GLSL = /* glsl */ `
uniform vec4 uCloudShadowParams[2];
uniform vec4 uCloudShadowLight[2];
uniform vec4 uCloudShadowWind[2];
uniform vec4 uCloudShadowBox[2];
uniform mat4 uCloudShadowMatrix[2];
uniform vec4 uCloudShadowGlobal;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
uniform highp sampler2D uCloudShadowMap0;
uniform highp sampler2D uCloudShadowMap1;
float rl_cloudShadowSlot(int slot, vec3 worldPos) {
    vec4 params = uCloudShadowParams[slot];
    if (params.x <= 0.0) return 1.0;
    vec4 L = uCloudShadowLight[slot];
    if (L.y < 0.05) return 1.0;
    vec3 p = (uCloudShadowMatrix[slot] * vec4(worldPos, 1.0)).xyz;
    // only what lies under the clouds is shaded by them
    float below = 1.0 - smoothstep(params.w - abs(params.z - params.w) * 0.25, params.w, p.y);
    if (below <= 0.0) return 1.0;
    vec3 q = p + L.xyz * ((params.z - p.y) / L.y);
    // box: generated x, live x, generated z, live z (the live box is a window, as in the read code)
    vec4 box = uCloudShadowBox[slot];
    vec2 a = vec2(q.x / box.y, q.z / box.w);
    vec2 gq = vec2(q.x / box.x, q.z / box.z);
    vec4 wind = uCloudShadowWind[slot];
    float fw = max(wind.w, 1e-3);
    float edge;
    if (wind.z > 0.5) {
        edge = 1.0 - smoothstep(1.0 - 2.0 * fw, 1.0, length(a) * 2.0);
    } else {
        edge = (1.0 - smoothstep(0.5 - fw, 0.5, abs(a.x))) * (1.0 - smoothstep(0.5 - fw, 0.5, abs(a.y)));
    }
    if (edge <= 0.0) return 1.0;
    vec2 uv = fract(gq - wind.xy + 0.5);
    vec2 m = slot == 0 ? textureLod(uCloudShadowMap0, uv, 0.0).rg : textureLod(uCloudShadowMap1, uv, 0.0).rg;
    float gate = clamp((L.w * 1.2 - m.g) / 0.2, 0.0, 1.0);
    float T = exp(-m.r * params.y * gate);
    return 1.0 - params.x * (1.0 - T) * edge * below;
}
float splatCloudShadowFactor(vec3 worldPos) {
    if (uCloudShadowGlobal.x < 0.5) return 1.0;
    return rl_cloudShadowSlot(0, worldPos) * rl_cloudShadowSlot(1, worldPos);
}
#else
float splatCloudShadowFactor(vec3 worldPos) { return 1.0; }
#endif
`;

export const CLOUD_SHADOW_WGSL = /* wgsl */ `
uniform uCloudShadowParams: array<vec4f, 2>;
uniform uCloudShadowLight: array<vec4f, 2>;
uniform uCloudShadowWind: array<vec4f, 2>;
uniform uCloudShadowBox: array<vec4f, 2>;
uniform uCloudShadowMatrix: array<mat4x4f, 2>;
uniform uCloudShadowGlobal: vec4f;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
var uCloudShadowMap0: texture_2d<f32>;
var uCloudShadowMap0Sampler: sampler;
var uCloudShadowMap1: texture_2d<f32>;
var uCloudShadowMap1Sampler: sampler;
fn rl_cloudShadowSlot(slot: i32, worldPos: vec3f) -> f32 {
    let params = uniform.uCloudShadowParams[slot];
    if (params.x <= 0.0) { return 1.0; }
    let L = uniform.uCloudShadowLight[slot];
    if (L.y < 0.05) { return 1.0; }
    let p = (uniform.uCloudShadowMatrix[slot] * vec4f(worldPos, 1.0)).xyz;
    let below = 1.0 - smoothstep(params.w - abs(params.z - params.w) * 0.25, params.w, p.y);
    if (below <= 0.0) { return 1.0; }
    let q = p + L.xyz * ((params.z - p.y) / L.y);
    let box = uniform.uCloudShadowBox[slot];
    let a = vec2f(q.x / box.y, q.z / box.w);
    let gq = vec2f(q.x / box.x, q.z / box.z);
    let wind = uniform.uCloudShadowWind[slot];
    let fw = max(wind.w, 1e-3);
    var edge: f32;
    if (wind.z > 0.5) {
        edge = 1.0 - smoothstep(1.0 - 2.0 * fw, 1.0, length(a) * 2.0);
    } else {
        edge = (1.0 - smoothstep(0.5 - fw, 0.5, abs(a.x))) * (1.0 - smoothstep(0.5 - fw, 0.5, abs(a.y)));
    }
    if (edge <= 0.0) { return 1.0; }
    let uv = fract(gq - wind.xy + 0.5);
    var m: vec2f;
    if (slot == 0) {
        m = textureSampleLevel(uCloudShadowMap0, uCloudShadowMap0Sampler, uv, 0.0).rg;
    } else {
        m = textureSampleLevel(uCloudShadowMap1, uCloudShadowMap1Sampler, uv, 0.0).rg;
    }
    let gate = clamp((L.w * 1.2 - m.g) / 0.2, 0.0, 1.0);
    let T = exp(-m.r * params.y * gate);
    return 1.0 - params.x * (1.0 - T) * edge * below;
}
fn splatCloudShadowFactor(worldPos: vec3f) -> f32 {
    if (uniform.uCloudShadowGlobal.x < 0.5) { return 1.0; }
    return rl_cloudShadowSlot(0, worldPos) * rl_cloudShadowSlot(1, worldPos);
}
#else
fn splatCloudShadowFactor(worldPos: vec3f) -> f32 { return 1.0; }
#endif
`;

/** Cloud-shadow uniform / texture names (shared with the cloud system and the placeholders). */
export const CLOUD_SHADOW_UNIFORMS = {
    params: 'uCloudShadowParams[0]',
    light: 'uCloudShadowLight[0]',
    wind: 'uCloudShadowWind[0]',
    box: 'uCloudShadowBox[0]',
    matrix: 'uCloudShadowMatrix[0]',
    global: 'uCloudShadowGlobal',
    maps: ['uCloudShadowMap0', 'uCloudShadowMap1'],
} as const;

/** Cloud layers whose shadows the scan can take at once. */
export const MAX_CLOUD_SHADOW_LAYERS = 2;
